import {
  CheckIcon,
  ChevronDownIcon,
  SearchIcon,
  SparklesIcon,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { cn } from '@/lib/utils';
import {
  DistinctExternalTaskNameSchema,
  ExternalTaskFilter,
  useExternalTaskRuleDistinctNamesQuery,
} from '@/services/api/firmsService';

interface PopularTaskNamesProps {
  firmId: string;
  selectedNames: string[];
  onAddName: (name: string) => void;
  onRemoveName: (name: string) => void;
  disabled?: boolean;
  /** Current external filter to scope suggestions to matching tasks */
  externalFilter?: ExternalTaskFilter;
}

interface SuggestionCardProps {
  suggestion: DistinctExternalTaskNameSchema;
  maxCount: number;
  isSelected: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

const SuggestionCard: React.FC<SuggestionCardProps> = ({
  suggestion,
  maxCount,
  isSelected,
  onToggle,
  disabled,
}) => {
  const progressWidth = Math.max(8, (suggestion.count / maxCount) * 100);

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={cn(
        'group relative w-full overflow-hidden rounded-xl border p-3.5 text-left transition-all duration-200',
        isSelected
          ? 'border-emerald-200 bg-emerald-50/80 hover:border-emerald-300 hover:bg-emerald-100/80'
          : 'border-slate-200 bg-white hover:border-blue-300 hover:shadow-md hover:shadow-blue-100/50',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      {/* Subtle background gradient on hover */}
      <div
        className={cn(
          'absolute inset-0 bg-gradient-to-br from-blue-50/0 to-blue-50/0 transition-all duration-300',
          !isSelected &&
            !disabled &&
            'group-hover:from-blue-50/50 group-hover:to-transparent',
        )}
      />

      <div className="relative">
        {/* Task name and status */}
        <div className="mb-2 flex items-start justify-between gap-2">
          <span
            className={cn(
              'line-clamp-2 text-sm font-medium leading-snug transition-colors',
              isSelected ? 'text-emerald-800' : 'text-slate-800',
            )}
            title={suggestion.name}
          >
            {suggestion.name}
          </span>
          {isSelected ? (
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500 shadow-sm">
              <CheckIcon className="h-3 w-3 text-white" strokeWidth={3} />
            </span>
          ) : (
            <span
              className={cn(
                'shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold transition-all duration-200',
                'bg-slate-100 text-slate-500',
                !disabled && 'group-hover:bg-blue-500 group-hover:text-white',
              )}
            >
              +
            </span>
          )}
        </div>

        {/* Progress bar and count */}
        <div className="space-y-1.5">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-500',
                isSelected
                  ? 'bg-emerald-400'
                  : 'bg-gradient-to-r from-blue-400 to-blue-500',
              )}
              style={{ width: `${progressWidth}%` }}
            />
          </div>
          <div className="flex items-center justify-between">
            <span
              className={cn(
                'text-xs font-medium',
                isSelected ? 'text-emerald-600' : 'text-slate-400',
              )}
            >
              {suggestion.count.toLocaleString()} task
              {suggestion.count !== 1 ? 's' : ''}
            </span>
            {isSelected && (
              <span className="text-xs font-medium text-emerald-600 group-hover:hidden">
                Added
              </span>
            )}
            {isSelected && (
              <span className="hidden text-xs font-medium text-emerald-700 group-hover:inline">
                Remove
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};

const SkeletonCard: React.FC = () => (
  <div className="rounded-xl border border-slate-200 bg-white p-3.5">
    <div className="mb-2 flex items-start justify-between">
      <div className="h-4 w-3/4 animate-pulse rounded bg-slate-200" />
      <div className="h-5 w-5 animate-pulse rounded-full bg-slate-200" />
    </div>
    <div className="space-y-1.5">
      <div className="h-1.5 w-full animate-pulse rounded-full bg-slate-100" />
      <div className="h-3 w-16 animate-pulse rounded bg-slate-200" />
    </div>
  </div>
);

export const PopularTaskNames: React.FC<PopularTaskNamesProps> = ({
  firmId,
  selectedNames,
  onAddName,
  onRemoveName,
  disabled = false,
  externalFilter,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(20);

  const {
    data: suggestions = [],
    isLoading,
    isError,
  } = useExternalTaskRuleDistinctNamesQuery({
    firmId,
    minCount: 2,
    // Only pass filter params if they have values
    ...(externalFilter?.assignee_names?.length && {
      assigneeNames: externalFilter.assignee_names,
    }),
    ...(externalFilter?.description_contains?.length && {
      descriptionContains: externalFilter.description_contains,
    }),
    ...(externalFilter?.name_contains?.length && {
      nameContains: externalFilter.name_contains,
    }),
    ...(externalFilter?.name_not_contains?.length && {
      nameNotContains: externalFilter.name_not_contains,
    }),
    ...(externalFilter?.statuses?.length && {
      statuses: externalFilter.statuses,
    }),
  });

  // Filter suggestions by search query
  const filteredSuggestions = useMemo(() => {
    if (!searchQuery.trim()) return suggestions;
    const query = searchQuery.toLowerCase();
    return suggestions.filter((s) => s.name.toLowerCase().includes(query));
  }, [suggestions, searchQuery]);

  // Get max count for progress bar scaling
  const maxCount = useMemo(
    () => Math.max(...suggestions.map((s) => s.count), 1),
    [suggestions],
  );

  // Count how many are already selected
  const selectedCount = useMemo(
    () => suggestions.filter((s) => selectedNames.includes(s.name)).length,
    [suggestions, selectedNames],
  );

  // Total tasks represented
  const totalTasks = useMemo(
    () => suggestions.reduce((sum, s) => sum + s.count, 0),
    [suggestions],
  );

  const handleToggleName = (name: string) => {
    if (selectedNames.includes(name)) {
      onRemoveName(name);
    } else {
      onAddName(name);
    }
  };

  // Don't show if no suggestions or error
  if (isError || (!isLoading && suggestions.length === 0)) {
    return null;
  }

  const visibleSuggestions = filteredSuggestions.slice(0, visibleCount);
  const hasMore = filteredSuggestions.length > visibleCount;
  const allSelected =
    suggestions.length > 0 && selectedCount === suggestions.length;

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border transition-all duration-300',
        isExpanded
          ? 'border-blue-200 bg-gradient-to-b from-blue-50/70 via-blue-50/30 to-white shadow-sm'
          : 'border-slate-200 bg-white',
        disabled && 'opacity-60',
      )}
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        disabled={disabled}
        className={cn(
          'flex w-full items-center justify-between px-4 py-3.5 transition-colors',
          !disabled && 'hover:bg-slate-50/50',
        )}
      >
        <div className="flex items-center gap-2.5">
          <div
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-lg transition-colors',
              isExpanded
                ? 'bg-blue-500 shadow-sm shadow-blue-200'
                : 'bg-slate-100',
            )}
          >
            <SparklesIcon
              className={cn(
                'h-4 w-4 transition-colors',
                isExpanded ? 'text-white' : 'text-slate-500',
              )}
            />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-900">
                Popular Task Names
              </span>
              {selectedCount > 0 && (
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                  {selectedCount} added
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500">
              {isLoading
                ? 'Loading suggestions...'
                : `${suggestions.length.toLocaleString()} patterns from ${totalTasks.toLocaleString()} tasks`}
            </p>
          </div>
        </div>
        <ChevronDownIcon
          className={cn(
            'h-5 w-5 text-slate-400 transition-transform duration-300',
            isExpanded && 'rotate-180',
          )}
        />
      </button>

      {/* Expandable Content */}
      <div
        className={cn(
          'grid transition-all duration-300 ease-out',
          isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          <div className="border-t border-slate-100/80 px-4 pb-4 pt-3">
            {/* Search Input */}
            <div className="relative mb-4">
              <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setVisibleCount(20); // Reset pagination on search
                }}
                placeholder="Search suggestions..."
                disabled={disabled || isLoading}
                className={cn(
                  'w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900',
                  'placeholder:text-slate-400',
                  'transition-all duration-150',
                  'focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100',
                  'disabled:cursor-not-allowed disabled:bg-slate-50',
                )}
              />
            </div>

            {/* All Selected Message */}
            {allSelected && !searchQuery && (
              <div className="mb-4 flex items-center justify-center rounded-lg bg-emerald-50 px-4 py-3">
                <CheckIcon className="mr-2 h-4 w-4 text-emerald-500" />
                <span className="text-sm font-medium text-emerald-700">
                  All popular names have been added
                </span>
              </div>
            )}

            {/* Suggestions Grid */}
            {isLoading ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            ) : filteredSuggestions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <SearchIcon className="mb-2 h-8 w-8 text-slate-300" />
                <p className="text-sm font-medium text-slate-600">
                  No matching suggestions
                </p>
                <p className="text-xs text-slate-400">
                  Try a different search term
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {visibleSuggestions.map((suggestion) => (
                    <SuggestionCard
                      key={suggestion.name}
                      suggestion={suggestion}
                      maxCount={maxCount}
                      isSelected={selectedNames.includes(suggestion.name)}
                      onToggle={() => handleToggleName(suggestion.name)}
                      disabled={disabled}
                    />
                  ))}
                </div>

                {/* Load More / Summary */}
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-xs text-slate-500">
                    Showing {visibleSuggestions.length} of{' '}
                    {filteredSuggestions.length}
                  </span>
                  {hasMore && (
                    <button
                      type="button"
                      onClick={() => setVisibleCount((prev) => prev + 20)}
                      disabled={disabled}
                      className={cn(
                        'text-sm font-medium text-blue-600 transition-colors',
                        !disabled && 'hover:text-blue-700',
                        disabled && 'cursor-not-allowed opacity-50',
                      )}
                    >
                      Load more
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
